// Debe reflejar exactamente la fórmula usada en la vista SQL
// `event_financials` y en `cost_lines_total()`
// (supabase/migrations/0009_cost_lines.sql), para que el resumen en vivo del
// formulario coincida con lo que calcula la base.

/** Por qué se multiplica cada línea de costo. */
export const PER_OPTIONS = [
  ["fijo", "fijo", null],
  ["chico", "por chico", "children_count"],
  ["profesor", "por profesor", "teachers_count"],
  ["souvenir", "por souvenir", "souvenirs_count"],
];

const COUNT_FIELD = Object.fromEntries(PER_OPTIONS.map(([per, , field]) => [per, field]));

/**
 * Cuántas veces se cobra esta línea, según las cantidades del evento.
 * `included` = a partir de qué cantidad cuenta: con included 25 y 32 chicos,
 * cuenta 7. Sirve para "chico extra", "profesor a partir del 3ro". Default 0.
 */
export function lineMultiplier(line, counts) {
  const field = COUNT_FIELD[line?.per];
  if (!field) return 1;
  const count = Number(counts?.[field]) || 0;
  const included = Number(line?.included) || 0;
  return Math.max(count - included, 0);
}

export function lineTotal(line, counts) {
  return (Number(line?.amount) || 0) * lineMultiplier(line, counts);
}

/** Suma de una lista de líneas —de costo o de precio— por las cantidades. */
export function linesTotal(lines, counts) {
  if (!Array.isArray(lines)) return 0;
  return lines.reduce((sum, line) => sum + lineTotal(line, counts), 0);
}

export const costLinesTotal = linesTotal;

export function eventCostTotal(event) {
  return linesTotal(event.cost_lines, event);
}

/**
 * El precio es la suma de sus líneas, como el costo. Las filas de la vista ya
 * traen `income` calculado; las de event_finance traen sólo price_lines.
 */
export function eventIncome(event) {
  if (Array.isArray(event?.price_lines)) return linesTotal(event.price_lines, event);
  return Number(event?.income) || 0;
}

export function eventProfit(event) {
  return eventIncome(event) - eventCostTotal(event);
}

/** Alguna línea de costo "a presupuestar" sigue sin monto. */
export function needsQuote(costLines) {
  return (Array.isArray(costLines) ? costLines : []).some((l) => l.quote && !(Number(l.amount) > 0));
}

// ---------- Cobranza ----------
//
// La regla es una y la asume la app: la seña entra al vender y el saldo se
// cobra el día del cumpleaños. Sólo se marca la excepción (balance_pending).
// Esta función es la única fuente de verdad del estado: la ficha, la lista y
// el panel la usan, así que no pueden decir cosas distintas.

export const COLLECTION_STATES = {
  sin_sena: ["Sin seña", "Todavía no entró nada."],
  senado: ["Señado", "Falta el saldo. Se cobra el día del cumple."],
  cobrado: ["Cobrado", "Seña y saldo cobrados."],
  debe: ["Debe", "El cumple ya pasó y el saldo no se cobró."],
};

export function collectionOf(event, today) {
  const price = eventIncome(event);
  const deposit = Number(event.deposit) || 0;
  const balance = Math.max(price - deposit, 0);
  const happened = Boolean(event.date) && event.date <= today;

  let state;
  if (deposit <= 0 && price > 0) state = "sin_sena";
  else if (!happened) state = "senado";
  else if (event.balance_pending) state = "debe";
  else state = "cobrado";

  // Lo que efectivamente entró, según la regla.
  const collected = deposit + (state === "cobrado" ? balance : 0);
  const pending = price - collected;

  return { price, deposit, balance, collected, pending: Math.max(pending, 0), state, happened };
}
